import type {
  APIBookingDraftServiceOptionParticipantPrice,
  ServiceRenderType,
  APIBookingDraftCarRentalService,
  APIBookingDraftCarRentalServiceOption,
} from '@/scopes/checkout/infrastructure/api/types';
import type { CheckoutCarRentalServiceOption, CheckoutCarRentalServiceOptionFeature } from '../../../domain/types';
import type { IconGlyph } from '@sunwebgroup/ui-components';
import { partial, pipe, prop } from 'ramda';

export interface ServiceCarRentalOptionMapper {
  map: (
    option: APIBookingDraftCarRentalServiceOption,
    service: APIBookingDraftCarRentalService
  ) => CheckoutCarRentalServiceOption;

  mapUpgradeOption: (
    option: APIBookingDraftCarRentalServiceOption,
    service: APIBookingDraftCarRentalService,
    selectedCarPrice: number
  ) => CheckoutCarRentalServiceOption;
}

export const createServiceCarRentalOptionMapper = () => {
  function map(option: APIBookingDraftCarRentalServiceOption, service: APIBookingDraftCarRentalService) {
    const imageUrlLarge = isValidUrl(option.imagePath?.large ?? '') ? option.imagePath?.large : '';
    const imageUrlSmall = isValidUrl(option.imagePath?.small ?? '') ? option.imagePath?.small : '';

    const carOption: CheckoutCarRentalServiceOption = {
      id: option.uniqueId ?? option.code,
      value: option.code,
      price: mapOptionPrice(option.price, option.prices ?? [], service.isPricePerPerson ?? false, service.renderType),
      image: imageUrlLarge ?? imageUrlSmall ?? '',
      carClass: option.carClass ?? '',
      label: option.carModel ?? '',
      conditionsUrl: option.conditionsUrl ?? '',
      features: mapFeatures(option) ?? [],
      extraFeatures: mapExtraFeatures(option) ?? [],
      equipments: mapEquipments(option) ?? [],
      selectedEquipments: option.selectedEquipments?.map(prop('id')) ?? [],
      selectedEquipmentsWithQuantity:
        option.selectedEquipments?.map((equipment) => ({
          id: equipment.id,
          quantity: equipment.quantitySelected ?? 1,
        })) ?? [],
      maxNumberOfEquipments: option.maxNumberOfEquipments ?? 0,
      driverId: option.driverId === 0 ? null : String(option.driverId),
      location: {
        icon: option.locationCode === 'AirportToAirport' ? ('IconAirplane' as IconGlyph) : null,
        text: option.location || '',
      },
      openJawFeeAmount: option.openJawFeeAmount ?? 0,
      availableOnRequest: option.availableOnRequest ?? false,
      canBeRemoved: option.isCancellable ?? false,
      canBeEdited: option.isSelfEditable ?? false,
      canBeUpgraded: option.canBeUpgraded ?? false,
      isIncluded: option.isIncluded ?? false,
    };
    return carOption;
  }

  function mapUpgradeOption(
    option: APIBookingDraftCarRentalServiceOption,
    service: APIBookingDraftCarRentalService,
    selectedCarPrice: number
  ) {
    const mappedOption = map(option, service);
    const priceDifference = option.price - selectedCarPrice;

    return { ...mappedOption, price: Math.abs(priceDifference), priceSymbol: getPriceSymbol(priceDifference) };
  }

  const getPriceSymbol = (priceDifference: number) => {
    if (priceDifference > 0) {
      return '+';
    }
    if (priceDifference < 0) {
      return '-';
    }
    return '';
  };

  function mapOptionPrice(
    price: number,
    prices: APIBookingDraftServiceOptionParticipantPrice[],
    isPricePerPerson: boolean,
    renderType: ServiceRenderType
  ): number {
    const pricesWithParticipantsFiltered = prices.filter((itemPrice) => itemPrice.participantIds.length > 0);

    if (
      !isPricePerPerson ||
      (isPricePerPerson && prices.length === 0) ||
      (renderType === 'PerParticipant' &&
        isPricePerPerson &&
        prices.length > 0 &&
        pricesWithParticipantsFiltered.length === 1)
    ) {
      return price;
    }

    return prices?.reduce((total, item) => total + item.price * item.participantIds.length, 0);
  }

  function mapEquipments(option: APIBookingDraftCarRentalServiceOption): { id: string; text: string }[] {
    const { equipments } = option;

    return (equipments || []).map((equipment) => ({
      id: equipment.id,
      text: equipment.text,
      price: equipment.price ?? 0,
      maxQuantity: equipment.maxQuantity ?? 1,
    }));
  }

  return { map, mapUpgradeOption };
};

export const isValidUrl = (urlString: string) => {
  let url;
  try {
    url = new URL(urlString);
  } catch (e) {
    return false;
  }
  return url.protocol === 'http:' || url.protocol === 'https:';
};

export function mapFeatures(option: APIBookingDraftCarRentalServiceOption) {
  return pipe(
    partial(mapPassengersFeature, [option]),
    partial(mapLuggageFeature, [option]),
    partial(mapDoorsFeature, [option])
  )([]);
}

export function mapExtraFeatures(option: APIBookingDraftCarRentalServiceOption) {
  return pipe(partial(mapHasAirconditionedFeature, [option]), partial(mapHasAutomaticGearFeature, [option]))([]);
}

function mapPassengersFeature(
  option: APIBookingDraftCarRentalServiceOption,
  features: CheckoutCarRentalServiceOptionFeature[]
) {
  if (!option.passengers) {
    return features;
  }
  return [...features, { glyph: 'IconOnePerson' as IconGlyph, text: option.passengers?.toString() }];
}

function mapLuggageFeature(
  option: APIBookingDraftCarRentalServiceOption,
  features: CheckoutCarRentalServiceOptionFeature[]
) {
  if (!option.luggage) {
    return features;
  }
  return [...features, { glyph: 'IconSuitcase' as IconGlyph, text: option.luggage?.toString() }];
}

function mapDoorsFeature(
  option: APIBookingDraftCarRentalServiceOption,
  features: CheckoutCarRentalServiceOptionFeature[]
) {
  if (!option.doors) {
    return features;
  }
  return [...features, { glyph: 'IconCarDoor' as IconGlyph, text: option.doors?.toString() }];
}

function mapHasAirconditionedFeature(
  option: APIBookingDraftCarRentalServiceOption,
  features: CheckoutCarRentalServiceOptionFeature[]
) {
  if (!('hasAirconditioned' in option)) {
    return features;
  }
  return [...features, { glyph: 'IconSnowflake' as IconGlyph, text: option.airconditionedText }];
}

function mapHasAutomaticGearFeature(
  option: APIBookingDraftCarRentalServiceOption,
  features: CheckoutCarRentalServiceOptionFeature[]
) {
  if (!('hasAutomaticGear' in option)) {
    return features;
  }
  return [...features, { glyph: 'IconTransmissionManual' as IconGlyph, text: option.carGearText }];
}
